// apps/lk/src/components/dashboard/EqBlock.tsx
'use client';
import { useState } from 'react';
import { useDescriptions } from './descriptions-context';
import { scoreColor, scoreBg } from './polarity';

type Desc = { title?: string; summary?: string; work?: string; people?: string; recommendation?: string };

const ASPECTS = [
  { key: 'selfEmotion',   descKey: 'eq.self_emotion',   name: 'Понимание своих эмоций',   hint: 'Осознаёте свои эмоциональные состояния, понимаете их причины' },
  { key: 'othersEmotion', descKey: 'eq.others_emotion', name: 'Распознавание чужих эмоций', hint: 'Считываете эмоции окружающих по мимике, голосу, поведению' },
  { key: 'useEmotion',    descKey: 'eq.use_emotion',    name: 'Использование эмоций',     hint: 'Превращаете эмоции в мотивацию и движущую силу' },
  { key: 'regulation',    descKey: 'eq.regulation',     name: 'Управление эмоциями',      hint: 'Контролируете импульсы, успокаиваетесь в стрессе' },
] as const;

function gradation(v: number): 'low' | 'mid' | 'high' {
  if (v >= 60) return 'high';
  if (v <= 40) return 'low';
  return 'mid';
}

function levelLabel(total: number): string {
  if (total >= 75) return 'Высокий';
  if (total >= 50) return 'Средний';
  return 'Требует развития';
}

/** Генерация заголовка-характеристики на основе профиля */
function profileTitle(strongKey: string, total: number): string {
  const map: Record<string, string> = {
    selfEmotion:   total >= 60 ? 'Тонко чувствующий' : 'Загадка для самого себя',
    othersEmotion: total >= 60 ? 'Эмпат' : 'Прямолинейный',
    useEmotion:    total >= 60 ? 'Горящий' : 'Спокойный мотор',
    regulation:    total >= 60 ? 'Невозмутимый' : 'Искренний',
  };
  return map[strongKey] ?? 'Эмоционально развивающийся';
}

/** Генерация инсаайт-абзаца */
function insightText(
  strongName: string, strongVal: number,
  weakName: string, weakVal: number,
  total: number,
): string {
  const gap = strongVal - weakVal;
  if (total >= 75) {
    return `Ваш эмоциональный интеллект развит гармонично. Вы хорошо понимаете и свои, и чужие эмоции и умеете ими управлять. Главная опора — «${strongName}» (${strongVal}).`;
  }
  if (gap >= 25) {
    return `Вы сильнее всего в «${strongName}» (${strongVal}), но сложнее даётся «${weakName}» (${weakVal}). Это асимметричный профиль: вы чувствуете больше, чем можете контролировать. Это не недостаток — а точка, с которой расти.`;
  }
  if (total <= 40) {
    return `Эмоциональная сфера — пока не ваш главный ресурс. Это нормально: EQ — навык, который развивается. Начните с малого — и вы удивитесь, как быстро пойдут изменения.`;
  }
  return `Ваш профиль достаточно ровный. Сильнее всего — «${strongName}» (${strongVal}), зона роста — «${weakName}» (${weakVal}). Разрыв небольшой, значит, EQ можно развивать сбалансированно.`;
}

/* ── Раскрывающийся бар аспекта ── */
function AspectBar({ name, hint, percent, desc }: { name: string; hint: string; percent: number; desc?: Desc }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 p-2.5 text-left hover:bg-slate-50 transition"
      >
        <div className="flex-1">
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium text-slate-700">{name}</span>
            <span className={`tabular-nums font-bold ${scoreColor(percent)}`}>{percent}</span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-200">
            <div className={`h-full rounded-full ${scoreBg(percent)}`} style={{ width: `${percent}%` }} />
          </div>
        </div>
        {desc && (
          <svg
            className={`text-slate-300 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`}
            width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        )}
      </button>
      {open && desc && (
        <div className="space-y-2 border-t border-slate-100 p-3 text-xs leading-relaxed text-slate-600">
          {desc.title && <p className="font-semibold text-slate-800">{desc.title}</p>}
          {desc.summary && <p>{desc.summary}</p>}
          {desc.work && <p><span className="font-medium text-slate-700">💼 На работе: </span>{desc.work}</p>}
          {desc.people && <p><span className="font-medium text-slate-700">👥 С людьми: </span>{desc.people}</p>}
          {desc.recommendation && <p className="text-amber-700"><span className="font-medium">💡 Совет: </span>{desc.recommendation}</p>}
        </div>
      )}
      {!desc && open && (
        <div className="border-t border-slate-100 p-3 text-xs text-slate-400">{hint}</div>
      )}
    </div>
  );
}

export function EqBlock({ data }: { data: any }) {
  const { get } = useDescriptions();
  if (!data?.aspects) return null;

  const aspects = data.aspects;
  const total: number = data.eqTotal;

  // Сортируем аспекты по убыванию
  const sorted = ASPECTS
    .map(a => ({ ...a, percent: aspects[a.key]?.percent ?? 0 }))
    .sort((a, b) => b.percent - a.percent);

  const strongest = sorted[0];
  const weakest = sorted[sorted.length - 1];

  // Получаем описания для каждого аспекта
  const descs: Record<string, Desc> = {};
  for (const a of ASPECTS) {
    const d = get('eq-wleis', a.descKey, gradation(aspects[a.key]?.percent ?? 0));
    if (d?.title) descs[a.key] = d;
  }

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/50">
      {/* ══ Заголовок-статья ══ */}
        <div className="mb-5 border-b border-slate-100 pb-4">
          <p className="text-xs font-medium uppercase tracking-wider text-indigo-500">Эмоциональный интеллект</p>
          <h3 className="mt-1 font-heading text-xl font-bold text-slate-900">
            {profileTitle(strongest.key, total)}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Ваш общий балл — {total} из 100.{' '}
            {insightText(
              strongest.name, strongest.percent,
              weakest.name, weakest.percent,
              total,
            )}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <span className="text-2xl font-bold text-indigo-600">{total}</span>
            <span className="text-sm text-slate-400">/100</span>
            <span className="ml-1 rounded-full bg-indigo-50 px-3 py-0.5 text-xs font-medium text-indigo-600">
              {levelLabel(total)}
            </span>
          </div>
        </div>

        {/* ══ Сильная сторона / Зона роста ══ */}
        <div className="mb-5 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <div className="mb-0.5 flex items-center gap-1.5">
              <span className="text-sm">💪</span>
              <span className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Сильная сторона</span>
            </div>
            <p className="text-sm font-bold text-emerald-900">{strongest.name}</p>
            <p className="mt-0.5 text-xs text-emerald-700">{strongest.percent}/100 · {strongest.hint}</p>
          </div>
          {weakest.key !== strongest.key && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <div className="mb-0.5 flex items-center gap-1.5">
                <span className="text-sm">🌱</span>
                <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">Зона роста</span>
              </div>
              <p className="text-sm font-bold text-amber-900">{weakest.name}</p>
              <p className="mt-0.5 text-xs text-amber-700">{weakest.percent}/100 · {weakest.hint}</p>
            </div>
          )}
        </div>

        {/* ══ Раскрывающиеся бары аспектов ══ */}
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          {'Все шкалы — нажмите для деталей'}
        </p>
        <div className="space-y-2">
          {ASPECTS.map(a => {
            const percent = aspects[a.key]?.percent ?? 0;
            return (
              <AspectBar
                key={a.key}
                name={a.name}
                hint={a.hint}
                percent={percent}
                desc={descs[a.key]}
              />
            );
          })}
        </div>
    </div>
  );
}
